import { Ref, ref } from "vue";
import { MstSyouninGroupsService } from "@/service/MstSyouninGroupsService";
import { MstSyouninGroup, MstSyouninGroupReq, MstSyouninGroupRes, MstSyouninGroupUser, SelectableGroupUser, SelectableSyouninUser, SyouninGroupCreateReq, SyouninGroupDetail, SyouninGroupUser, SyouninUser, UpsertSyouninGroupUserReq, Users } from "@/types/SyouninGroup";
import { MstSyouninGroupUsersService } from "@/service/MstSyouninGroupUsersService";
import { DialogService } from "@/service/DialogService";
import { UtilService } from "@/service/UtilService";

export function useSyouninGroup() {
  const groupService = new MstSyouninGroupsService();
  const groupUserService = new MstSyouninGroupUsersService();

  // 承認グループ詳細
  const syouninGroupDetail: Ref<SyouninGroupDetail | null> = ref(null)

  // 承認グループを取得する
  const getSyouninGroupDetail = async (groupId: number) => {
    syouninGroupDetail.value = await groupService.getGroupById(groupId)
  }

  const isCreating = ref(false);

  const createSyouninGroup = async (syouninType: number, groupName: string, userList: MstSyouninGroupUser[], groupUserList: SelectableGroupUser[]) => {
    isCreating.value = true;

    DialogService.nowloading('登録中...');

    const req: SyouninGroupCreateReq = ({
      syounin_type: syouninType,
      group_name: groupName,
      syounin_users: userList,
      group_users: groupUserList,
    });

    try{
      const res = await groupService.createSyouninGroup(req)

      if (res) {

        // エラーメッセージを表示
        if(res.data.errorMessage){
          DialogService.error({
            text: res.data.errorMessage
          }, true);
          isCreating.value = false;
          return false;
        }

        DialogService.info({
          text: `承認者グループを登録しました`
        }, true);
        isCreating.value = false;
        return true;
      } else {
        DialogService.error({
          text: '予期せぬ事態が発生し登録に失敗しました'
        }, true);
        isCreating.value = false;
        return false;
      }
    } catch(e: any) {
      await UtilService.showResponseErrorDialog(e)
      isCreating.value = false;
      return false;
    }

    isCreating.value = false;
  }

  const deleteSyouninGroup = async (id: number): Promise<Boolean> => {
    const dialogResult = await DialogService.question({
      text: '選択した承認グループを削除しますか？',
      showCancelButton: true,
      confirmButtonText: '削除する',
      cancelButtonText: 'キャンセル'
    })

    if (!dialogResult.isConfirmed) {
      return false
    }

    // 処理中ダイアログを表示
    DialogService.nowloading('削除中...')
    const srv = new MstSyouninGroupsService()
    const res = await srv.deleteSyouninGroup(id)
    if (res) {
      // 削除に成功した場合
      await DialogService.success({
        text: '選択した承認グループを削除しました'
      }, true)
      return true
    } else {
      // 削除に失敗した場合
      await DialogService.error({
        text: '削除に失敗しました'
      }, true)
      return false
    }
  }
  // 承認グループ更新
  const updateSyouninGroup = async (syouninInfo: MstSyouninGroupRes) => {
    // 入力チェック
    if (!syouninInfo.syounin_type || !syouninInfo.group_name || syouninInfo.group_name === '') {
      await DialogService.error({
        text: '承認グループ名を入力してください'
      });
      return;
    };

    const dialogResult = await DialogService.question({
      text: 'グループ名を更新しますか？',
      showCancelButton: true,
      confirmButtonText: '更新します',
      cancelButtonText: 'キャンセル',
    })

    if (dialogResult.isConfirmed) {
      isUpdating.value = true;
      const srv = new MstSyouninGroupsService();
      DialogService.nowloading('更新中...');

      const params = {
        mst_syounin_group_id: syouninInfo.mst_syounin_group_id,
        syounin_type: syouninInfo.syounin_type,
        group_name: syouninInfo.group_name
      };

      const res = await srv.updateSyouninGroup(params)
      if (res) {
        await DialogService.info({
          text: `承認者グループを更新しました`
        }, true)
        isUpdating.value = false
        return true
      } else {
        await DialogService.error({
          text: '予期せぬ事態が発生し更新に失敗しました'
        }, true);
        isUpdating.value = false
        return false
      }
    }
    else {
      isUpdating.value = false
      return false

    }
  }

  // 検索した承認グループのリスト
  const syouninGroupList = ref<MstSyouninGroupRes[]>([]);
  const getSyouninGroupsList = async (req: MstSyouninGroupReq) => {
    syouninGroupList.value = [];
    // is_disableSelectGroupBtn.value = true;

    const res = await groupService.searchGroupAndUser(req)
    if (res !== null) {
      syouninGroupList.value = res;
    } else {
      DialogService.error({
        text: '予期せぬ事態が発生し検索に失敗しました'
      }, true);
    }
  };

  // 承認グループの承認者／確認者
  const selectableSyouninUserList: Ref<SelectableSyouninUser[]> = ref([])
  const getSelectableSyouninUserList = async (groupId?: number) => {
    const res = await groupService.getSelectableSyouninUserList(groupId)

    // 選択用編集画面で取得した承認者用ユーザーは初期値は承認者とする
    res.forEach(user => {
      selectableSyouninUserList.value.push({
        ...user,
        is_delete: false,
        is_select: false,
        is_syounin: true,
        active: false,
        syounin_order: 0,
      })
    });
  }

  const addSyouninUser = async (groupId: number, userList: MstSyouninGroupUser[]) => {
    // 入力チェック
    if (userList.length === 0) {
      await DialogService.error({
        text: '承認者・確認者を選択してください'
      });
      return
    };

    const dialogResult = await DialogService.question({
      text: '指定されたユーザーを承認者・確認者に追加しますか？',
      showCancelButton: true,
      confirmButtonText: '追加します',
      cancelButtonText: 'キャンセル',
    })

    if (!dialogResult.isConfirmed) {
      return false
    }

    isAdding.value = true;
    const srv = new MstSyouninGroupsService();
    DialogService.nowloading('更新中...');
    console.log('addSyouninUsersInfo', userList)
    const params: UpsertSyouninGroupUserReq = ({
      mst_syounin_group_id: groupId,
      users: userList
    });

    if (await srv.addSyouninUsers(params)) {
      await DialogService.info({
        text: `承認者・確認者を追加しました`
      }, true);
      isAdding.value = false;
      return true
    } else {
      await DialogService.error({
        text: '予期せぬ事態が発生し更新に失敗しました'
      }, true)
      isAdding.value = false;
      return false;
    }
  }

  // 承認者・確認者を更新する
  const isUpdating = ref(false);
  const updateSyouninUsers = async (syouninInfo: MstSyouninGroupRes) => {
    const dialogResult = await DialogService.question({
      text: '承認者・確認者を更新しますか？',
      showCancelButton: true,
      confirmButtonText: '更新します',
      cancelButtonText: 'キャンセル',
    })

    if (!dialogResult.isConfirmed) {
      return false
    }

    isUpdating.value = true
    const srv = new MstSyouninGroupsService()
    DialogService.nowloading('更新中...')

    const res = await srv.updateSyouninUsers(syouninInfo)
    if (res) {
      await DialogService.info({
        text: `承認者・確認者を更新しました`
      }, true)
      isUpdating.value = false
      return true
    } else {
      await DialogService.error({
        text: '予期せぬ事態が発生し更新に失敗しました'
      }, true)
      isUpdating.value = false
      return false
    }
  }

  /** 承認者・確認者を削除します */
  const deleteSyouninUser = async (syouninUser: SyouninUser) => {
    const dialogResult = await DialogService.question({
      text: '選択したユーザーを削除しますか？編集途中の承認順序がある場合、変更内容はリセットされます。',
      showCancelButton: true,
      confirmButtonText: '削除します',
      cancelButtonText: 'キャンセル',
    })

    if (!dialogResult.isConfirmed) {
      return false
    }
    // 処理中ダイアログを表示
    DialogService.nowloading('削除中...')

    let mstSyouninUserId = syouninUser.mst_syounin_group_syounin_user_id;
    let mstSyouninGroupId = syouninUser.mst_syounin_group_id;

    const res = await groupService.deleteSyouninUser(mstSyouninUserId, mstSyouninGroupId)
    if (res) {
      // 削除に成功した場合
      await DialogService.success({
        text: '選択したユーザーを削除しました'
      }, true);
      return true
    } else {
      // 削除に失敗した場合
      await DialogService.error({
        text: '削除に失敗しました'
      }, true)
      return false
    }
  }

  // 承認グループの所属者
  const selectableGroupUserList: Ref<SelectableGroupUser[]> = ref([])
  const getSelectableSyouninGroupUserList = async (syouninTypeId?: number) => {
    const res = await groupUserService.getSelectableSyouninGroupUserList(syouninTypeId)

    // 選択用編集画面で取得した承認者用ユーザーは初期値は承認者とする
    res.forEach(user => {
      selectableGroupUserList.value.push({
        ...user,
        is_select: false,
        active: false,
        is_delete: false,
      })
    });
  }

  /** 承認グループに所属するユーザーを追加します */
  const isAdding = ref(false);
  const addGroupUser = async (groupId: number, userList: SelectableGroupUser[]) => {
    const dialogResult = await DialogService.question({
      text: '指定されたユーザーを承認グループに追加しますか？',
      showCancelButton: true,
      confirmButtonText: '追加します',
      cancelButtonText: 'キャンセル',
    })

    if (!dialogResult.isConfirmed) {
      return false
    }

    isAdding.value = true;

    const srv = new MstSyouninGroupUsersService();
    DialogService.nowloading('登録中...');
    console.log('addSyouninUsersInfo', userList)

    const params: UpsertSyouninGroupUserReq = {
      mst_syounin_group_id: groupId,
      users: userList
    };

    if (await srv.addSyouninGroupUsers(params)) {
      await DialogService.info({
        text: `承認グループにユーザーを登録しました`
      }, true)
      isAdding.value = false;
      return true
    } else {
      await DialogService.error({
        text: '予期せぬ事態が発生し登録に失敗しました'
      }, true);

      isAdding.value = false;
      return false
    }
  }


  /** 所属ユーザーを削除します */
  const deleteGroupUser = async (groupUser: SyouninGroupUser) => {
    const dialogResult = await DialogService.question({
      text: '選択したユーザーを削除しますか？',
      showCancelButton: true,
      confirmButtonText: '削除します',
      cancelButtonText: 'キャンセル',
    })

    if (!dialogResult.isConfirmed) {
      return false
    }
    // 処理中ダイアログを表示
    DialogService.nowloading('削除中...')

    const srv = new MstSyouninGroupUsersService();
    let mstSyouninGroupUserId = groupUser.mst_syounin_group_user_id;
    let mstSyouninGroupId = groupUser.mst_syounin_group_id;

    if (await srv.deleteSyouninGroupUser(mstSyouninGroupUserId, mstSyouninGroupId)) {
      // 削除に成功した場合
      await DialogService.success({
        text: '選択したユーザーを削除しました'
      }, true)
      return true
    } else {
      // 削除に失敗した場合
      await DialogService.error({
        text: '削除に失敗しました'
      }, true)
      return false
    }
  }

  return {
    syouninGroupDetail,
    isCreating,
    createSyouninGroup,
    isUpdating,
    updateSyouninGroup,
    deleteSyouninGroup,
    getSyouninGroupDetail,
    getSyouninGroupsList,
    syouninGroupList,
    getSelectableSyouninUserList,
    selectableSyouninUserList,
    addSyouninUser,
    updateSyouninUsers,
    deleteSyouninUser,
    getSelectableSyouninGroupUserList,
    selectableGroupUserList,
    addGroupUser,
    deleteGroupUser,
    isAdding
  }
}
